import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Plus, X, GripVertical } from 'lucide-react';

/* ───────────── Canvas layout constants ─────────────
 *
 * Cards are positioned absolutely. Their conceptual position is
 * (group_name, row_index); pixel coordinates are derived. On drag-end
 * we snap to the nearest (column, row) and persist via the parent.
 *
 * The free-form x/y columns on influence_map_people are reserved for a
 * future "free-positioning" mode; in Ship 2 they're always null after
 * a drag (so saved positions remain CRM-portable across screen sizes).
 */
export const CANVAS_PADDING = 20;
export const COL_W = 220;
export const COL_GAP = 24;
export const CARD_W = 188;
export const CARD_H = 90;
export const ROW_H = CARD_H + 16;
// Tall enough to host a 17px bold title with breathing room above and
// below — previous 36px was too cramped and the title was getting lost.
export const COL_HEAD_H = 52;
export const UNASSIGNED_KEY = '__unassigned__';
// Width of the left gutter that hosts row labels. Reserved only when
// the map actually has labelled rows — otherwise we don't waste space.
// Wide enough to show full row names (e.g. "Executive") without clipping.
export const ROW_LABEL_W = 150;
const DRAG_THRESHOLD_PX = 4;

// Normalise mouse and touch events to a single { clientX, clientY }. Touch
// events carry their point in `touches` while in-flight and in
// `changedTouches` at touchend, so we fall back through both.
function pointFromEvent(e) {
  if (e.touches && e.touches.length) {
    return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
  }
  if (e.changedTouches && e.changedTouches.length) {
    return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
  }
  return { clientX: e.clientX, clientY: e.clientY };
}

// Reserve the left gutter only when row labels exist. This lets maps
// without rows still feel tight, while maps with rows give the labels
// a clear column to live in.
export function rowLabelGutter(map) {
  return (map?.rows && map.rows.length > 0) ? ROW_LABEL_W : 0;
}

export const STATUS_COLORS = {
  green: '#1f9d34',
  yellow: '#f2c200',
  red: '#d0202a',
};

// Selectable arrow colors. The key is what we persist on each link; `red` is
// the default for arrows created before this feature existed.
export const ARROW_COLORS = {
  red: '#d0202a',
  orange: '#e8800c',
  green: '#1f9d34',
};

export function linkColorKey(link) {
  return ARROW_COLORS[link?.color] ? link.color : 'red';
}

export function linkColorHex(link) {
  return ARROW_COLORS[linkColorKey(link)];
}

// ─── Geometry helpers ────────────────────────────────────────────────────

// Effective list of columns shown on the canvas: declared groups + an
// "Unassigned" column when there are placed people without a group.
export function effectiveColumns(map) {
  const groups = map?.groups || [];
  const cols = [...groups];
  const hasUnassigned = (map?.people || []).some(p => !p.group_name);
  if (hasUnassigned || cols.length === 0) cols.push(UNASSIGNED_KEY);
  return cols;
}

// All x-coordinate helpers take a `gutter` arg so they account for the
// row-label column when present. Callers compute the gutter via
// rowLabelGutter(map) and thread it through.
export function columnX(colIdx, gutter = 0) {
  return CANVAS_PADDING + gutter + colIdx * (COL_W + COL_GAP);
}

export function rowY(rowIdx) {
  return CANVAS_PADDING + COL_HEAD_H + rowIdx * ROW_H;
}

// Free vertical position of a ROW LABEL. Rows are decoupled from cards: a row
// can be dragged anywhere and left there without moving any card. The stored
// value (map.row_y[ri]) is an offset in px below the first-row baseline;
// when unset it falls back to the default ri*ROW_H slot.
export function rowOffsetOf(map, ri) {
  const v = map?.row_y?.[ri];
  return (v == null) ? ri * ROW_H : v;
}

// Absolute canvas y of a row label's row top (same coordinate space as rowY).
export function rowTopOf(map, ri) {
  return CANVAS_PADDING + COL_HEAD_H + rowOffsetOf(map, ri);
}

export function cardCenter(pos) {
  return { cx: pos.x + CARD_W / 2, cy: pos.y + CARD_H / 2 };
}

// Pixel position of a card based on its group/row.
export function positionOf(person, columns, gutter = 0) {
  const groupKey = person.group_name || UNASSIGNED_KEY;
  let colIdx = columns.indexOf(groupKey);
  if (colIdx < 0) colIdx = columns.length - 1; // shouldn't happen, but safe
  const x = columnX(colIdx, gutter) + (COL_W - CARD_W) / 2;
  const y = rowY(person.row_index || 0);
  return { x, y };
}

// Reverse: pixel (x, y) → snapped (column index, row index).
export function snapToGrid(px, py, columns, gutter = 0) {
  const totalColW = COL_W + COL_GAP;
  const relativeX = px - CANVAS_PADDING - gutter + totalColW / 2;
  let colIdx = Math.floor(relativeX / totalColW);
  colIdx = Math.max(0, Math.min(columns.length - 1, colIdx));
  const relativeY = py - CANVAS_PADDING - COL_HEAD_H;
  let rowIdx = Math.max(0, Math.round(relativeY / ROW_H));
  return { colIdx, rowIdx };
}

export function canvasDimensions(map) {
  const cols = effectiveColumns(map);
  const gutter = rowLabelGutter(map);
  // Height must clear the lowest card AND the lowest free-positioned row label,
  // since the two are independent now.
  const maxCardRow = (map?.people || []).reduce(
    (m, p) => Math.max(m, p.row_index || 0),
    -1
  );
  const maxCardBottom = (maxCardRow + 1) * ROW_H;
  const maxRowBottom = (map?.rows || []).reduce(
    (m, _r, i) => Math.max(m, rowOffsetOf(map, i) + ROW_H),
    0
  );
  const gridH = Math.max(maxCardBottom, maxRowBottom, ROW_H);
  // +1 row of breathing room below
  const width = CANVAS_PADDING * 2 + gutter + cols.length * (COL_W + COL_GAP) - COL_GAP;
  const height = CANVAS_PADDING * 2 + COL_HEAD_H + gridH + ROW_H;
  return {
    width: Math.max(width, 600),
    height: Math.max(height, 420),
    cols,
    gutter,
  };
}

// ─── Arrow geometry ───────────────────────────────────────────────────────

// Pick anchor points on the source/target card edges so the line enters
// the closest side. Returns { sx, sy, tx, ty } in absolute coordinates.
export function arrowAnchors(srcPos, tgtPos) {
  const src = cardCenter(srcPos);
  const tgt = cardCenter(tgtPos);
  const dx = tgt.cx - src.cx;
  const dy = tgt.cy - src.cy;
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);
  // Tuck both endpoints 6px inside the card edge so the line end (and its
  // arrowhead) sits visibly within the card border rather than flush on it.
  // The arrowhead layer renders above the cards, so this overlap shows.
  const INSET = 6;
  let sx, sy, tx, ty;
  if (adx >= ady) {
    // Horizontal-dominant: exit through left/right
    sx = dx > 0 ? srcPos.x + CARD_W - INSET : srcPos.x + INSET;
    sy = src.cy;
    tx = dx > 0 ? tgtPos.x + INSET : tgtPos.x + CARD_W - INSET;
    ty = tgt.cy;
  } else {
    // Vertical-dominant: exit through top/bottom
    sx = src.cx;
    sy = dy > 0 ? srcPos.y + CARD_H - INSET : srcPos.y + INSET;
    tx = tgt.cx;
    ty = dy > 0 ? tgtPos.y + INSET : tgtPos.y + CARD_H - INSET;
  }
  return { sx, sy, tx, ty };
}

// Quadratic curve between the two anchors, with the control point
// perpendicular to the midpoint to give a gentle arc. `flip` bows the
// curve to the opposite side.
export function curvePath(sx, sy, tx, ty, flip = false) {
  const mx = (sx + tx) / 2;
  const my = (sy + ty) / 2;
  const dx = tx - sx;
  const dy = ty - sy;
  const len = Math.hypot(dx, dy) || 1;
  // perpendicular vector, scaled by 12% of length for a subtle bow
  const ox = -dy / len;
  const oy = dx / len;
  const bow = Math.min(len * 0.12, 40) * (flip ? -1 : 1);
  const cx = mx + ox * bow;
  const cy = my + oy * bow;
  return `M${sx} ${sy} Q${cx} ${cy} ${tx} ${ty}`;
}

// ─── React component ──────────────────────────────────────────────────────

function InfluenceCanvas({
  map,
  canEdit,
  dimmedIds,
  onPersonClick,
  onMovePerson,
  onCreateLink,
  onSelectLink,
  onRenameGroup,
  onDeleteGroup,
  onReorderGroupToIndex,
  onRenameRow,
  onDeleteRow,
  onMoveRowOffset,
  zoom = 1, // CSS zoom on the canvas; pointer math divides by it
}) {
  const canvasRef = useRef(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  // Column-header rename state: { name } when a column is being renamed
  const [renamingColumn, setRenamingColumn] = useState(null);
  // Row-label rename state: index of the row being renamed, or null
  const [renamingRow, setRenamingRow] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Tracks whether the current mousedown→mouseup gesture included
  // enough movement to count as a drag. React fires onClick after a
  // drag-and-drop (because mousedown and mouseup happened on the same
  // card), and without this guard the click handler would re-open the
  // edit modal. Reset on every mousedown.
  const gestureWasDragRef = useRef(false);

  // ephemeral interaction state
  const [dragInfo, setDragInfo] = useState(null);
  // { personId, startMouse: {x,y}, startPos: {x,y}, currentPos: {x,y}, hasMoved }

  // Swimlane-header drag state. Separate from card dragging so the two
  // gestures never interfere. While set:
  //   { groupName, originalIndex, startMouse: {x,y}, pointerX, hasMoved, targetIndex }
  // pointerX is canvas-relative (matches the columnX() coordinate space) and
  // drives both the drop indicator and the targetIndex computation.
  const [groupDrag, setGroupDrag] = useState(null);

  // Row-label drag state (FREE vertical positioning — the label moves to wherever
  // it's dropped and stays; cards never move with it):
  //   { rowIndex, startY, pointerY, hasMoved }
  const [rowDrag, setRowDrag] = useState(null);

  const [connectFrom, setConnectFrom] = useState(null); // personId | null
  const [connectMouse, setConnectMouse] = useState(null); // { x, y } | null
  const [hoverPersonId, setHoverPersonId] = useState(null);

  // Touch devices have no hover, so the connect "+" handle (normally
  // revealed on hover) would be unreachable. Detect a coarse pointer and
  // keep the handle visible on those devices.
  const coarsePointer = useMemo(
    () => typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: none)').matches,
    []
  );

  const dims = useMemo(() => canvasDimensions(map), [map]);
  const columns = dims.cols;
  const gutter = dims.gutter;
  const rowLabels = map?.rows || [];
  // Named swimlanes occupy column indices 0..groupCount-1; the optional
  // "Unassigned" column is always pinned after them, so reorder arrows
  // only operate within this range.
  const groupCount = (map?.groups || []).length;

  // While a swimlane header is mid-drag, derive the live "preview" column
  // order so the OTHER lanes slide to make room (the same feel as dashboard
  // cards). The dragged lane itself is rendered at its original slot plus a
  // translateX that follows the pointer, so its preview slot is left open.
  const headerDragging = groupDrag?.hasMoved ? groupDrag.groupName : null;
  const dragDx = headerDragging ? (groupDrag.pointerX - groupDrag.startMouse.x) : 0;
  const previewColumns = useMemo(() => {
    if (!headerDragging) return columns;
    const arr = [...columns];
    const from = arr.indexOf(headerDragging);
    if (from < 0) return columns;
    arr.splice(from, 1);
    const clamped = Math.max(0, Math.min(arr.length, groupDrag.targetIndex));
    arr.splice(clamped, 0, headerDragging);
    return arr;
  }, [columns, headerDragging, groupDrag]);

  // Row drag is free vertical movement of the label only. The dragged label
  // follows the pointer (translateY by rowDragDy); cards and other labels stay
  // exactly where they are.
  const rowCount = rowLabels.length;
  const rowDragging = rowDrag?.hasMoved ? rowDrag.rowIndex : null;
  const rowDragDy = rowDragging != null ? (rowDrag.pointerY - rowDrag.startY) : 0;

  // Person positions, derived from (group_name, row_index). When dragging a
  // card, override its position with the in-flight pixel. When dragging a
  // swimlane header, lanes reflow horizontally. Row drags never move cards.
  const personPositions = useMemo(() => {
    const pos = new Map();
    (map?.people || []).forEach(p => {
      if (headerDragging && p.group_name === headerDragging) {
        const base = positionOf(p, columns, gutter); // original slot
        pos.set(p.id, { x: base.x + dragDx, y: base.y });
      } else if (headerDragging) {
        pos.set(p.id, positionOf(p, previewColumns, gutter));
      } else {
        pos.set(p.id, positionOf(p, columns, gutter));
      }
    });
    if (dragInfo?.currentPos && dragInfo.hasMoved) {
      pos.set(dragInfo.personId, dragInfo.currentPos);
    }
    return pos;
  }, [map, columns, previewColumns, headerDragging, dragDx, gutter, dragInfo]);

  // ── Drag handlers ────────────────────────────────────────────────────

  // Shared drag-start for both mouse and touch. `clientX/clientY` are the
  // viewport coordinates of the press.
  const beginCardGesture = useCallback((clientX, clientY, person) => {
    if (!canEdit) return;
    // Start a fresh gesture — reset the drag-detection ref so the
    // upcoming onClick can tell drag from click.
    gestureWasDragRef.current = false;
    // If we're already in connect mode, the card click handler resolves it
    if (connectFrom) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const startMouse = { x: (clientX - rect.left) / zoomRef.current, y: (clientY - rect.top) / zoomRef.current };
    const startPos = positionOf(person, columns, gutter);
    setDragInfo({
      personId: person.id,
      startMouse,
      startPos,
      currentPos: startPos,
      hasMoved: false,
    });
  }, [canEdit, columns, gutter, connectFrom]);

  const onCardMouseDown = useCallback((e, person) => {
    if (e.button !== 0) return;
    if (canEdit && !connectFrom) e.preventDefault();
    beginCardGesture(e.clientX, e.clientY, person);
  }, [canEdit, connectFrom, beginCardGesture]);

  const onCardTouchStart = useCallback((e, person) => {
    if (!e.touches || e.touches.length !== 1) return;
    // Don't preventDefault here — a stationary tap must still fire onClick
    // to open the editor. We suppress page-scroll in the touchmove handler
    // once a real drag is detected.
    beginCardGesture(e.touches[0].clientX, e.touches[0].clientY, person);
  }, [beginCardGesture]);

  // Mousemove on the document (so drags don't get lost when the cursor
  // leaves the canvas) — only mounted when there's a drag or connect.
  useEffect(() => {
    if (!dragInfo && !connectFrom) return;

    const handleMove = (e) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const pt = pointFromEvent(e);
      const cx = (pt.clientX - rect.left) / zoomRef.current;
      const cy = (pt.clientY - rect.top) / zoomRef.current;

      // While dragging a card via touch, stop the page/canvas from
      // scrolling so the finger stays glued to the card.
      if (dragInfo && e.cancelable && e.touches) e.preventDefault();

      if (dragInfo) {
        const dx = cx - dragInfo.startMouse.x;
        const dy = cy - dragInfo.startMouse.y;
        const hasMoved = dragInfo.hasMoved
          || Math.hypot(dx, dy) > DRAG_THRESHOLD_PX;
        // Latch the ref so the upcoming React onClick can detect this
        // gesture was a drag, not a click.
        if (hasMoved) gestureWasDragRef.current = true;
        setDragInfo({
          ...dragInfo,
          currentPos: {
            x: dragInfo.startPos.x + dx,
            y: dragInfo.startPos.y + dy,
          },
          hasMoved,
        });
      } else if (connectFrom) {
        setConnectMouse({ x: cx, y: cy });
      }
    };

    const handleUp = async (e) => {
      if (dragInfo) {
        const wasDrag = dragInfo.hasMoved;
        const finishedDrag = dragInfo;
        setDragInfo(null);
        // Drag completed: snap to grid and persist. The React onClick
        // that fires next will be suppressed by the gestureWasDragRef
        // check, so we don't double-open the editor.
        if (wasDrag) {
          const rect = canvasRef.current?.getBoundingClientRect();
          if (!rect) return;
          const pt = pointFromEvent(e);
          const cx = (pt.clientX - rect.left) / zoomRef.current;
          const cy = (pt.clientY - rect.top) / zoomRef.current;
          const finalX = finishedDrag.startPos.x + (cx - finishedDrag.startMouse.x);
          const finalY = finishedDrag.startPos.y + (cy - finishedDrag.startMouse.y);
          const { colIdx, rowIdx } = snapToGrid(finalX, finalY, columns, gutter);
          const newGroup = columns[colIdx];
          const person = (map.people || []).find(p => p.id === finishedDrag.personId);
          if (person) {
            const newGroupName = newGroup === UNASSIGNED_KEY ? null : newGroup;
            const changed = (person.group_name || null) !== newGroupName
              || (person.row_index || 0) !== rowIdx;
            if (changed) {
              await onMovePerson(finishedDrag.personId, {
                group_name: newGroupName,
                row_index: rowIdx,
                x: null,
                y: null,
              });
            }
          }
        }
        // No-movement case: do nothing here. The React onClick on the
        // card will fire and call onPersonClick.
      }
    };

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setDragInfo(null);
        setConnectFrom(null);
        setConnectMouse(null);
      }
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    // touchmove must be non-passive so handleMove can preventDefault() to
    // block scroll while a card is being dragged.
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleUp);
    document.addEventListener('touchcancel', handleUp);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
      document.removeEventListener('touchcancel', handleUp);
      document.removeEventListener('keydown', handleKey);
    };
  }, [dragInfo, connectFrom, columns, gutter, map, onMovePerson, onPersonClick]);

  // ── Swimlane-header drag handlers ─────────────────────────────────────

  // Final 0-based position of the dragged swimlane among the named groups,
  // given a canvas-relative pointer x. By contract: the number of OTHER
  // named columns (exclude the dragged one and the Unassigned column) whose
  // horizontal centre is left of the pointer, clamped to [0, groupCount-1].
  const computeTargetIndex = useCallback((pointerX, originalIndex) => {
    let count = 0;
    for (let ci = 0; ci < groupCount; ci++) {
      if (ci === originalIndex) continue;
      const centerX = columnX(ci, gutter) + COL_W / 2;
      if (centerX < pointerX) count++;
    }
    return Math.max(0, Math.min(groupCount - 1, count));
  }, [groupCount, gutter]);

  // Shared drag-start for both mouse and touch on a column-name span.
  // `colKey` is the group name; `ci` its current display index.
  const beginGroupGesture = useCallback((clientX, colKey, ci) => {
    if (!canEdit || colKey === UNASSIGNED_KEY) return;
    // Reset the drag-detection ref so the upcoming double-click / click on
    // the name can tell drag from a stationary press.
    gestureWasDragRef.current = false;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pointerX = (clientX - rect.left) / zoomRef.current;
    setGroupDrag({
      groupName: colKey,
      originalIndex: ci,
      startMouse: { x: pointerX },
      pointerX,
      hasMoved: false,
      targetIndex: ci,
    });
  }, [canEdit]);

  const onColumnNameMouseDown = useCallback((e, colKey, ci) => {
    if (e.button !== 0) return;
    if (!canEdit || colKey === UNASSIGNED_KEY) return;
    e.preventDefault();
    beginGroupGesture(e.clientX, colKey, ci);
  }, [canEdit, beginGroupGesture]);

  const onColumnNameTouchStart = useCallback((e, colKey, ci) => {
    if (!e.touches || e.touches.length !== 1) return;
    if (!canEdit || colKey === UNASSIGNED_KEY) return;
    // Don't preventDefault here — a stationary tap must still allow the
    // double-tap-to-rename. We block scroll in the touchmove handler once a
    // real drag is detected.
    beginGroupGesture(e.touches[0].clientX, colKey, ci);
  }, [canEdit, beginGroupGesture]);

  // Document-level move/up listeners, mounted only while a header drag is
  // live (mirrors the card-drag effect above).
  useEffect(() => {
    if (!groupDrag) return;

    const handleMove = (e) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const pt = pointFromEvent(e);
      const pointerX = (pt.clientX - rect.left) / zoomRef.current;

      // While dragging via touch, stop the page/canvas from scrolling.
      if (e.cancelable && e.touches) e.preventDefault();

      const dx = pointerX - groupDrag.startMouse.x;
      const hasMoved = groupDrag.hasMoved || Math.abs(dx) > DRAG_THRESHOLD_PX;
      if (hasMoved) gestureWasDragRef.current = true;
      setGroupDrag({
        ...groupDrag,
        pointerX,
        hasMoved,
        targetIndex: computeTargetIndex(pointerX, groupDrag.originalIndex),
      });
    };

    const handleUp = () => {
      const finished = groupDrag;
      setGroupDrag(null);
      if (!finished.hasMoved) return; // stationary press → let click/dblclick run
      const targetIndex = computeTargetIndex(finished.pointerX, finished.originalIndex);
      if (onReorderGroupToIndex) {
        // Parent handles the no-op (targetIndex === originalIndex) safely.
        onReorderGroupToIndex(finished.groupName, targetIndex);
      }
    };

    const handleKey = (e) => {
      if (e.key === 'Escape') setGroupDrag(null);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleUp);
    document.addEventListener('touchcancel', handleUp);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
      document.removeEventListener('touchcancel', handleUp);
      document.removeEventListener('keydown', handleKey);
    };
  }, [groupDrag, computeTargetIndex, onReorderGroupToIndex]);

  // ── Row-label drag handlers (free vertical positioning) ───────────────

  const beginRowGesture = useCallback((clientY, ri) => {
    if (!canEdit) return;
    gestureWasDragRef.current = false;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pointerY = (clientY - rect.top) / zoomRef.current;
    setRowDrag({
      rowIndex: ri,
      startY: pointerY,
      pointerY,
      hasMoved: false,
    });
  }, [canEdit]);

  const onRowHandleMouseDown = useCallback((e, ri) => {
    if (e.button !== 0 || !canEdit) return;
    e.preventDefault();
    beginRowGesture(e.clientY, ri);
  }, [canEdit, beginRowGesture]);

  const onRowHandleTouchStart = useCallback((e, ri) => {
    if (!e.touches || e.touches.length !== 1 || !canEdit) return;
    beginRowGesture(e.touches[0].clientY, ri);
  }, [canEdit, beginRowGesture]);

  useEffect(() => {
    if (!rowDrag) return;

    const handleMove = (e) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const pt = pointFromEvent(e);
      const pointerY = (pt.clientY - rect.top) / zoomRef.current;
      if (e.cancelable && e.touches) e.preventDefault();
      const dy = pointerY - rowDrag.startY;
      const hasMoved = rowDrag.hasMoved || Math.abs(dy) > DRAG_THRESHOLD_PX;
      if (hasMoved) gestureWasDragRef.current = true;
      setRowDrag({ ...rowDrag, pointerY, hasMoved });
    };

    const handleUp = () => {
      const finished = rowDrag;
      setRowDrag(null);
      if (!finished.hasMoved) return; // stationary press → let dblclick run
      // Free positioning: new offset = current offset + drag delta, clamped ≥ 0.
      const dy = finished.pointerY - finished.startY;
      const newOffset = Math.max(0, Math.round(rowOffsetOf(map, finished.rowIndex) + dy));
      if (onMoveRowOffset) onMoveRowOffset(finished.rowIndex, newOffset);
    };

    const handleKey = (e) => {
      if (e.key === 'Escape') setRowDrag(null);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleUp);
    document.addEventListener('touchcancel', handleUp);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
      document.removeEventListener('touchcancel', handleUp);
      document.removeEventListener('keydown', handleKey);
    };
  }, [rowDrag, map, onMoveRowOffset]);

  // ── Connect handlers ──────────────────────────────────────────────────

  const startConnect = useCallback((personId) => {
    if (!canEdit) return;
    setConnectFrom(personId);
    setConnectMouse(null);
  }, [canEdit]);

  const handleCardClickForConnect = useCallback(async (person) => {
    // Only fires if a connect is armed and we're not in the middle of a drag.
    if (!connectFrom || dragInfo) return false;
    if (person.id === connectFrom) {
      setConnectFrom(null);
      setConnectMouse(null);
      return true;
    }
    const src = connectFrom;
    const tgt = person.id;
    setConnectFrom(null);
    setConnectMouse(null);
    await onCreateLink(src, tgt);
    return true;
  }, [connectFrom, dragInfo, onCreateLink]);

  // Empty-area click cancels a connect-in-progress
  const handleCanvasMouseDown = useCallback((e) => {
    // Only act if the actual target is the canvas backdrop (not a card/arrow)
    if (e.target !== canvasRef.current) return;
    if (connectFrom) {
      setConnectFrom(null);
      setConnectMouse(null);
    }
  }, [connectFrom]);

  // ── Render ────────────────────────────────────────────────────────────

  // The arrow line + click target. Rendered on the layer BEHIND the cards
  // so the body of the line passes behind any card it isn't connected to.
  // Arrowheads are drawn separately (renderArrowHead) on a layer in FRONT
  // of the cards so the ends stay visible on top of the cards they connect.
  const renderArrowLine = (link) => {
    const srcPos = personPositions.get(link.source_person_id);
    const tgtPos = personPositions.get(link.target_person_id);
    if (!srcPos || !tgtPos) return null;
    const { sx, sy, tx, ty } = arrowAnchors(srcPos, tgtPos);
    const dimmed = dimmedIds?.has(link.source_person_id)
      && dimmedIds?.has(link.target_person_id);
    const opacity = dimmed ? 0.18 : 1;
    const strokeWidth = (link.strength || 2) * 1.4 + 0.5;
    const d = curvePath(sx, sy, tx, ty, !!link.curve_flipped);
    return (
      <g key={link.id} className="im-arrow" opacity={opacity}>
        {/* Wider invisible hit area for easier clicking */}
        <path
          d={d}
          stroke="transparent"
          strokeWidth="16"
          fill="none"
          style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
          onClick={(e) => { e.stopPropagation(); onSelectLink && onSelectLink(link); }}
        />
        <path
          d={d}
          stroke={linkColorHex(link)}
          strokeWidth={strokeWidth}
          fill="none"
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  };

  // The front-of-card overlay for a link. Two paths:
  //  1. The line clipped to the two connected card rectangles — this makes
  //     the inset portion (the ~6px the line runs inside each connected
  //     card) visible on top of the card at BOTH ends, not just the head.
  //     It's clipped to the connected cards only, so the line still passes
  //     behind any unconnected card in the middle.
  //  2. The arrowhead(s), unclipped, so they poke out beyond the card edge.
  const renderArrowHead = (link) => {
    const srcPos = personPositions.get(link.source_person_id);
    const tgtPos = personPositions.get(link.target_person_id);
    if (!srcPos || !tgtPos) return null;
    const { sx, sy, tx, ty } = arrowAnchors(srcPos, tgtPos);
    const dimmed = dimmedIds?.has(link.source_person_id)
      && dimmedIds?.has(link.target_person_id);
    const opacity = dimmed ? 0.18 : 1;
    const colorKey = linkColorKey(link);
    const strokeWidth = (link.strength || 2) * 1.4 + 0.5;
    const d = curvePath(sx, sy, tx, ty, !!link.curve_flipped);
    const clipId = `im-arrow-clip-${link.id}`;
    // When either connected card is hovered, lift the WHOLE line onto the
    // front layer so the full arrow comes into view (over any card it
    // crosses). Otherwise only its overlap with the two connected cards
    // shows on top, and the body stays behind unconnected cards.
    const hovered = hoverPersonId != null
      && (link.source_person_id === hoverPersonId || link.target_person_id === hoverPersonId);
    return (
      <g key={link.id} opacity={opacity}>
        <clipPath id={clipId}>
          <rect x={srcPos.x} y={srcPos.y} width={CARD_W} height={CARD_H} />
          <rect x={tgtPos.x} y={tgtPos.y} width={CARD_W} height={CARD_H} />
        </clipPath>
        {/* Line: full on hover, else only where it overlaps connected cards. */}
        <path
          d={d}
          stroke={linkColorHex(link)}
          strokeWidth={strokeWidth}
          fill="none"
          clipPath={hovered ? undefined : `url(#${clipId})`}
          style={{ pointerEvents: 'none' }}
        />
        {/* Arrowheads — unclipped so the point extends past the card edge. */}
        <path
          d={d}
          stroke="transparent"
          strokeWidth={strokeWidth}
          fill="none"
          markerStart={link.bidirectional ? `url(#im-arrow-head-${colorKey})` : undefined}
          markerEnd={`url(#im-arrow-head-${colorKey})`}
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  };

  const renderPreviewArrow = () => {
    if (!connectFrom || !connectMouse) return null;
    const srcPos = personPositions.get(connectFrom);
    if (!srcPos) return null;
    const src = cardCenter(srcPos);
    return (
      <path
        d={`M${src.cx} ${src.cy} L${connectMouse.x} ${connectMouse.y}`}
        stroke="#1f9d34"
        strokeWidth="2"
        strokeDasharray="6 4"
        fill="none"
        style={{ pointerEvents: 'none' }}
      />
    );
  };

  return (
    <div
      ref={canvasRef}
      className={`im-canvas${connectFrom ? ' im-canvas-connecting' : ''}`}
      style={{
        position: 'relative',
        width: dims.width,
        height: dims.height,
        zoom,
      }}
      onMouseDown={handleCanvasMouseDown}
    >
      {/* Row labels in the left gutter (only when the map has rows). Drag a
          row's grip handle to move it freely up/down — it stays wherever it's
          dropped, and the cards never move with it (the user repositions cards
          separately). */}
      {rowLabels.map((label, ri) => {
        const isRenaming = renamingRow === ri;
        const isDraggedRow = rowDragging === ri;
        const rowTop = rowTopOf(map, ri) + (CARD_H - 28) / 2;
        return (
          <div
            key={`row-${ri}`}
            className={
              'im-row-head' +
              (isDraggedRow ? ' im-row-dragging' : '')
            }
            style={{
              left: CANVAS_PADDING - 4,
              top: rowTop,
              width: ROW_LABEL_W,
              height: 28,
              transform: isDraggedRow ? `translateY(${rowDragDy}px) scale(1.02)` : undefined,
            }}
          >
            {isRenaming ? (
              <input
                className="im-row-rename-input"
                value={renameValue}
                autoFocus
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => {
                  const next = renameValue.trim();
                  setRenamingRow(null);
                  if (next && next !== label && onRenameRow) {
                    onRenameRow(ri, next);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.target.blur(); }
                  if (e.key === 'Escape') { setRenamingRow(null); }
                }}
              />
            ) : (
              <>
                {canEdit && (
                  <button
                    type="button"
                    className="im-row-drag-handle"
                    title="Drag to reorder row"
                    aria-label="Drag to reorder row"
                    onMouseDown={(e) => onRowHandleMouseDown(e, ri)}
                    onTouchStart={(e) => onRowHandleTouchStart(e, ri)}
                  >
                    <GripVertical size={13} />
                  </button>
                )}
                <span
                  className="im-row-name"
                  title={canEdit ? 'Double-click to rename' : ''}
                  onDoubleClick={() => {
                    if (!canEdit) return;
                    // Suppress the dblclick that fires right after a reorder.
                    if (gestureWasDragRef.current) { gestureWasDragRef.current = false; return; }
                    setRenamingRow(ri);
                    setRenameValue(label);
                  }}
                >
                  {label}
                </span>
                {canEdit && (
                  <button
                    type="button"
                    className="im-row-x"
                    title="Delete row (cards below shift up)"
                    aria-label="Delete row"
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={() => onDeleteRow && onDeleteRow(ri)}
                  >
                    <X size={12} />
                  </button>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Column backdrops + headers. While a header is being dragged, the
          non-dragged lanes reflow to the preview order (with a transition,
          via .im-col-reflow), and the dragged lane follows the pointer via a
          translateX transform + lift styling — matching the dashboard cards. */}
      {columns.map((colKey, ci) => {
        const isUnassigned = colKey === UNASSIGNED_KEY;
        const isRenaming = renamingColumn === colKey;
        const isDraggedCol = headerDragging === colKey;
        // Dragged lane stays anchored at its original slot and is offset by
        // dragDx; every other lane slides to its slot in the preview order.
        const colLeft = isDraggedCol
          ? columnX(ci, gutter)
          : columnX(headerDragging ? previewColumns.indexOf(colKey) : ci, gutter);
        const reflowing = !!headerDragging && !isDraggedCol;
        return (
          <React.Fragment key={colKey}>
            <div
              className={'im-column-bg' + (reflowing ? ' im-col-reflow' : '')}
              style={{
                left: colLeft,
                top: CANVAS_PADDING + COL_HEAD_H - 8,
                width: COL_W,
                height: dims.height - CANVAS_PADDING - COL_HEAD_H,
                transform: isDraggedCol ? `translateX(${dragDx}px)` : undefined,
              }}
            />
            <div
              className={
                'im-column-head-wrap' +
                (isDraggedCol ? ' im-column-dragging' : '') +
                (reflowing ? ' im-col-reflow' : '')
              }
              style={{
                left: colLeft,
                top: CANVAS_PADDING,
                width: COL_W,
                transform: isDraggedCol
                  ? `translateX(${dragDx}px) scale(1.02)`
                  : undefined,
              }}
            >
              {canEdit && !isUnassigned && !isRenaming && (
                <button
                  type="button"
                  className="im-column-drag-handle"
                  title="Drag to reorder swimlane"
                  aria-label="Drag to reorder swimlane"
                  onMouseDown={(e) => onColumnNameMouseDown(e, colKey, ci)}
                  onTouchStart={(e) => onColumnNameTouchStart(e, colKey, ci)}
                >
                  <GripVertical size={14} />
                </button>
              )}
              {isRenaming ? (
                <input
                  className="im-column-rename-input"
                  value={renameValue}
                  autoFocus
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={() => {
                    const next = renameValue.trim();
                    setRenamingColumn(null);
                    if (next && next !== colKey && onRenameGroup) {
                      onRenameGroup(colKey, next);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.target.blur(); }
                    if (e.key === 'Escape') { setRenamingColumn(null); }
                  }}
                />
              ) : (
                <span
                  className="im-column-name"
                  title={canEdit && !isUnassigned ? 'Double-click to rename' : ''}
                  onDoubleClick={() => {
                    if (!canEdit || isUnassigned) return;
                    // A drag latches gestureWasDragRef; suppress the dblclick
                    // that fires right after a reorder gesture.
                    if (gestureWasDragRef.current) { gestureWasDragRef.current = false; return; }
                    setRenamingColumn(colKey);
                    setRenameValue(colKey);
                  }}
                >
                  {isUnassigned ? 'Unassigned' : colKey}
                </span>
              )}
              {canEdit && !isUnassigned && !isRenaming && (
                <button
                  type="button"
                  className="im-column-x"
                  title="Delete swimlane (cards move to Unassigned)"
                  aria-label="Delete swimlane"
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onClick={() => onDeleteGroup && onDeleteGroup(colKey)}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </React.Fragment>
        );
      })}

      {/* SVG arrow layer — lines only, drawn BEHIND the cards */}
      <svg
        className="im-arrow-layer"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          overflow: 'visible',
        }}
      >
        <g style={{ pointerEvents: 'auto' }}>
          {(map?.links || []).map(renderArrowLine)}
        </g>
        {renderPreviewArrow()}
      </svg>

      {/* Cards */}
      {(map?.people || []).map(person => {
        const pos = personPositions.get(person.id);
        if (!pos) return null;
        const isDragging = dragInfo?.personId === person.id && dragInfo.hasMoved;
        // During a swimlane header OR row drag: cards in the dragged band move
        // with it (lifted above the rest); cards elsewhere reflow smoothly.
        const inDraggedBand =
          (!!headerDragging && person.group_name === headerDragging) ||
          (rowDragging != null && (person.row_index || 0) === rowDragging);
        const reflowingCard =
          (!!headerDragging && person.group_name !== headerDragging) ||
          (rowDragging != null && (person.row_index || 0) !== rowDragging);
        const isHover = hoverPersonId === person.id;
        const isConnectSource = connectFrom === person.id;
        const isConnectTarget = connectFrom && connectFrom !== person.id;
        const dimmed = dimmedIds?.has(person.id);
        const statusColor = person.relationship_status
          ? STATUS_COLORS[person.relationship_status] || '#94a3b8'
          : '#94a3b8';
        const statusTextColor = person.relationship_status === 'yellow' ? '#5a4b00' : '#ffffff';
        const dispDisplay = person.disposition == null
          ? '·'
          : (person.disposition > 0 ? `+${person.disposition}` : String(person.disposition));
        return (
          <div
            key={person.id}
            className={[
              'im-person-card',
              isDragging ? 'dragging' : '',
              isConnectSource ? 'connect-source' : '',
              isConnectTarget ? 'connect-target' : '',
              dimmed ? 'dimmed' : '',
              reflowingCard ? 'im-col-reflow' : '',
            ].filter(Boolean).join(' ')}
            style={{
              left: pos.x,
              top: pos.y,
              width: CARD_W,
              height: CARD_H,
              cursor: connectFrom
                ? (isConnectSource ? 'pointer' : 'crosshair')
                : (canEdit ? 'grab' : 'pointer'),
              opacity: dimmed ? 0.28 : (inDraggedBand ? 0.6 : 1),
              zIndex: isDragging ? 50 : (inDraggedBand ? 20 : 3),
            }}
            onMouseDown={(e) => onCardMouseDown(e, person)}
            onTouchStart={(e) => onCardTouchStart(e, person)}
            onClick={async (e) => {
              // Suppress click when this gesture was a drag.
              if (gestureWasDragRef.current) {
                gestureWasDragRef.current = false;
                return;
              }
              if (await handleCardClickForConnect(person)) return;
              // Plain click (no drag, no connect) → open editor
              onPersonClick && onPersonClick(person.id);
            }}
            onMouseEnter={() => setHoverPersonId(person.id)}
            onMouseLeave={() => setHoverPersonId(null)}
            role="button"
            tabIndex={0}
          >
            <div className="im-pc-name" title={person.name}>{person.name}</div>
            {person.title && (
              <div className="im-pc-title" title={person.title}>{person.title}</div>
            )}
            <div className="im-pc-cells">
              <div
                className="im-pc-cell status"
                style={{ background: statusColor, color: statusTextColor }}
                title={`Disposition ${dispDisplay}, status ${person.relationship_status || 'unset'}`}
              >
                {dispDisplay}
              </div>
              <div className="im-pc-cell" title={person.influence_role ? `Influence: ${person.influence_role}` : 'Influence not set'}>
                {person.influence_role || '·'}
              </div>
              <div className="im-pc-cell" title={person.social_style ? `Social style: ${person.social_style}` : 'Social style not set'}>
                {person.social_style || '·'}
              </div>
            </div>

            {/* Connect handle ("+") — appears on hover, only when editable
                and not already in a connect-from-this-card state. */}
            {canEdit && (isHover || coarsePointer) && !connectFrom && !isDragging && (
              <button
                type="button"
                className="im-connect-handle"
                onMouseDown={(e) => { e.stopPropagation(); }}
                onTouchStart={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); startConnect(person.id); }}
                title="Draw an influence arrow from this card to another"
                aria-label="Connect from this card"
              >
                <Plus size={9} />
              </button>
            )}
          </div>
        );
      })}

      {/* SVG arrowhead layer — heads only, drawn IN FRONT of the cards so
          the ends show on top of the cards they connect to. Cards are
          z-index 3, so this must sit above them (it has pointerEvents:none,
          so it never blocks card clicks or the connect handle). */}
      <svg
        className="im-arrow-head-layer"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          overflow: 'visible',
          zIndex: 4,
        }}
      >
        <defs>
          {Object.entries(ARROW_COLORS).map(([key, hex]) => (
            <marker
              key={key}
              id={`im-arrow-head-${key}`}
              markerWidth="11"
              markerHeight="11"
              refX="9"
              refY="5"
              orient="auto-start-reverse"
              markerUnits="userSpaceOnUse"
            >
              <path d="M0,0 L10,5 L0,10 Z" fill={hex} />
            </marker>
          ))}
        </defs>
        {(map?.links || []).map(renderArrowHead)}
      </svg>

      {/* Empty-state hint when no one's on the map yet */}
      {(map?.people?.length || 0) === 0 && (
        <div className="im-canvas-empty">
          No one on the map yet — place a contact from the drawer to start.
        </div>
      )}
    </div>
  );
}

export default InfluenceCanvas;
